import React from 'react';
import { Link as RouterLink, useLocation, useNavigate } from 'react-router-dom';
import { FileText, LogOut } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { marketingUrl } from '../lib/marketingUrls';
import {
  DRAWER_WIDTH,
  NAV_SECTIONS,
  getNavItemsBySection,
  type NavItem,
  type NavSection,
} from '../lib/navConfig';
import { cn } from '../lib/utils';
import LanguageSelect from './LanguageSelect';
import { Avatar, AvatarFallback } from './ui/avatar';

interface SidebarProps {
  mobileOpen: boolean;
  onMobileClose: () => void;
  isDesktop?: boolean;
}

const MAIN_SECTIONS: NavSection[] = ['workspace', 'resources'];

const Sidebar: React.FC<SidebarProps> = ({ mobileOpen, onMobileClose, isDesktop = false }) => {
  const { isAuthenticated, user, logout } = useAuth();
  const { t } = useLanguage();
  const location = useLocation();
  const navigate = useNavigate();
  const itemsBySection = getNavItemsBySection(isAuthenticated);
  const accountItems = itemsBySection.get('account') ?? [];

  const closeOnNavigate = () => {
    if (!isDesktop) {
      onMobileClose();
    }
  };

  const renderNavItem = (item: NavItem) => {
    const Icon = item.icon;

    if (item.type === 'marketing' && item.marketingPath) {
      return (
        <a
          key={item.id}
          href={marketingUrl(item.marketingPath)}
          onClick={closeOnNavigate}
          className="flex items-center gap-3 rounded-lg px-3 py-2 text-sm text-foreground hover:bg-muted"
        >
          <Icon className="h-4 w-4" />
          <span>{t(item.labelKey)}</span>
        </a>
      );
    }

    if (!item.path) return null;

    const isActive = location.pathname === item.path;
    return (
      <RouterLink
        key={item.id}
        to={item.path}
        onClick={closeOnNavigate}
        className={cn(
          'flex items-center gap-3 rounded-lg px-3 py-2 text-sm',
          isActive ? 'bg-primary/10 font-medium text-primary' : 'text-foreground hover:bg-muted',
        )}
      >
        <Icon className="h-4 w-4" />
        <span>{t(item.labelKey)}</span>
      </RouterLink>
    );
  };

  const drawerContent = (
    <div className="flex h-full flex-col bg-background">
      <RouterLink
        to="/documents"
        onClick={closeOnNavigate}
        className="flex h-16 shrink-0 items-center gap-2 border-b px-4 text-base font-medium"
      >
        <FileText className="h-5 w-5 text-primary" />
        mySign.az
      </RouterLink>

      <nav className="flex-1 space-y-4 overflow-y-auto p-3 pt-4">
        {MAIN_SECTIONS.map((sectionId) => {
          const section = NAV_SECTIONS.find((entry) => entry.id === sectionId);
          const items = itemsBySection.get(sectionId) ?? [];
          if (!section || items.length === 0) return null;

          return (
            <div key={section.id}>
              <p className="mb-1 px-3 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                {t(section.labelKey)}
              </p>
              <div className="space-y-1">{items.map(renderNavItem)}</div>
            </div>
          );
        })}
      </nav>

      <div className="mt-auto border-t p-3">
        {isAuthenticated && user && (
          <div className="mb-3">
            <div className="mb-3 flex items-center gap-3 px-3">
              <Avatar className="h-10 w-10 shrink-0">
                <AvatarFallback>{user.name?.[0]?.toUpperCase() || 'U'}</AvatarFallback>
              </Avatar>
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-semibold">
                  {user.name} {user.surname}
                </p>
                <p className="truncate text-xs text-muted-foreground">{user.email}</p>
              </div>
            </div>
            {accountItems.length > 0 && (
              <div className="space-y-1">
                {accountItems.map(renderNavItem)}
                <button
                  type="button"
                  onClick={() => {
                    logout();
                    navigate('/login');
                    closeOnNavigate();
                  }}
                  className="flex w-full items-center gap-3 rounded-lg px-3 py-2 text-left text-sm text-foreground hover:bg-muted"
                >
                  <LogOut className="h-4 w-4" />
                  {t('logout')}
                </button>
              </div>
            )}
          </div>
        )}

        <div className="mb-3 px-3">
          <p className="mb-2 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
            {t('language')}
          </p>
          <LanguageSelect triggerClassName="w-full" />
        </div>

        <p className="px-3 text-xs text-muted-foreground">{t('footerCopyright')}</p>
      </div>
    </div>
  );

  return (
    <>
      <aside
        className={cn(
          'fixed left-0 top-0 z-30 hidden h-screen border-r bg-background md:block',
        )}
        style={{ width: DRAWER_WIDTH }}
      >
        {drawerContent}
      </aside>
      {mobileOpen && !isDesktop && (
        <div className="fixed inset-0 z-50 md:hidden">
          <div className="absolute inset-0 bg-black/50" onClick={onMobileClose} />
          <aside
            className="relative h-full border-r bg-background shadow-xl"
            style={{ width: DRAWER_WIDTH }}
          >
            {drawerContent}
          </aside>
        </div>
      )}
    </>
  );
};

export default Sidebar;
export { DRAWER_WIDTH };
